import {
    GridCell,
    GridColumn,
    GridRow,
    ICellEditor,
    ICellEditorWithText,
    IExternalEditor,
    IGridColumnEditor,
    RowCollection
} from '@Common/Controls/SimpleGridLight';
import { Button, IButtonParameters } from '@Common/Controls';
import { defaultHeight } from '@Common/Controls/SimpleGridLight/Editors/EditorProps';
import { safeDestroys } from '@Common/Core/Destroyable';

//TODO: rename to ColumnButtonEditor
export class GridButtonEditor implements IGridColumnEditor {
    private readonly setting: Required<IGridButtonEditorSetting>;

    constructor(setting: IGridButtonEditorSetting) {
        this.setting = _.defaults(setting, buttonCellEditorDefaultProperty);
    }

    public getEditor(row: GridRow, column: GridColumn): ICellEditor {
        return new CellButtonEditor(row.getCell(column), this.setting);
    }

    public buildEditorForAll(column: GridColumn): void {
        _.noop();
    }

    public refreshAll(rows: RowCollection): void {
        _.noop();
    }
}

export interface IButtonEditorCellPainterArg {
    readonly cell: GridCell;
    readonly button: Button;
    readonly view: JQuery;
}

export class CellButtonEditor implements ICellEditor, ICellEditorWithText {
    public readonly button: Button;
    public readonly view: JQuery;
    private _isInActiveMode = false;

    constructor(private readonly cell: GridCell, private readonly prop: Required<IGridButtonEditorSetting>) {
        this.cell.tableCell().addClass('visibleEditor');
        const buttonSetting = _.defaults(this.prop.buttonSetting, { text: prop.buttonText });
        buttonSetting.css = $.extend({ float: 'right', height: `${defaultHeight}px` }, buttonSetting.css);

        if (!this.prop.buttonText) {
            buttonSetting.className = (buttonSetting.className || '') + ' grid-command-btn';
        }

        this.button = new Button(buttonSetting);

        this.view = $('<div>', {
            css: {
                float: 'left',
                overflow: 'hidden',
                'text-overflow': 'ellipsis',
                'white-space': 'nowrap',
                'min-height': '1px',
                'line-height': '25px'
            }
        });

        if (this.prop.displayData) {
            this.button.getControl().css('width', '');
            this.button.getControl().hide();
        } else {
            this.view.hide();
            this.button.getControl().css('width', '100%');
        }

        const container = $('<div>');
        container.append(this.view);
        container.append(this.button.getControl());
        const clear = $('<div>', { css: { clear: 'both' } });
        container.append(clear);
        this.button.onClick((): void => {
            this.buttonClick();
        });
        this.refresh();
        container.appendTo(this.cell.tableCell());
    }

    private buttonClick(): void {
        const externalEditor = this.prop.fn(this.cell, this.button);
        if (typeof externalEditor === 'object' && externalEditor != null) {
            this._isInActiveMode = true;
            externalEditor.onChanged((newValue: any): void => {
                this.cell.value(newValue);
                this._isInActiveMode = false;
            });
        }
    }

    public beginEdit(): void {
        if (this.prop.displayData) {
            const but = this.button.getControl();
            but.show();
            const btnWidth = this.button.getControl().outerWidth(true) ?? 0;
            this.view.css('width', 'calc(100% - {0}px)'.format(btnWidth + 2));
        }
    }

    public endEdit(): void {
        if (this.prop.displayData) {
            this.view.css('width', '100%');
            this.button.getControl().hide();
        }
    }

    public refresh(): void {
        const painterArg = this as unknown as IButtonEditorCellPainterArg;
        if (this.prop.displayData) {
            this.view.text(this.prop.cellViewPainter(painterArg));
        } else {
            this.prop.cellViewPainter(painterArg);
        }

        this.button.visible(!this.cell.readonly());
    }

    public isInActiveMode(): boolean {
        return this._isInActiveMode;
    }

    public readonly(value: boolean): void {
        this.button.enabled(value);
    }

    public getText(): string {
        return this.view.text();
    }

    public destroy(): void {
        safeDestroys(this.button, this.view);
    }
}

export interface IGridButtonEditorSetting {
    fn?: (cell: GridCell, button: Button) => IExternalEditor | void;
    buttonText?: string;
    displayData?: boolean;
    cellViewPainter?: (arg: IButtonEditorCellPainterArg) => string;
    buttonSetting?: IButtonParameters;
}

const buttonCellEditorDefaultProperty: Required<IGridButtonEditorSetting> = {
    fn: $.noop,
    buttonText: '...',
    displayData: false,
    cellViewPainter: ({ cell }: IButtonEditorCellPainterArg): any => {
        if (cell.getColumn().dataIndex()) {
            if (!_.isNil(cell.value())) {
                return cell.value().toString();
            }
        }
        return '';
    },
    buttonSetting: {}
};
